'use client'

import { formatLongString } from '@morpho-org/morpho-ts'
import { cn } from '@morpho-org/ui-v6/utils'
import { forwardRef, type HTMLAttributes } from 'react'

import { Avatar } from './Avatar'

export interface CuratorCellProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Curator name */
  name: string
  /** Curator image URL */
  image?: string | null
  /** Maximum length for name before truncation */
  maxLength?: number
}

/**
 * CuratorCell component - Tailwind replica of dapp-ui-v5 CuratorCell
 *
 * Displays a curator with avatar and name.
 *
 * @example
 * ```tsx
 * <CuratorCell
 *   name="Gauntlet"
 *   image="https://cdn.morpho.org/curators/gauntlet.png"
 * />
 * ```
 */
const CuratorCell = forwardRef<HTMLDivElement, CuratorCellProps>(
  ({ name, image, maxLength = 30, className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('gap-xs flex items-center', className)} {...props}>
        {image && <Avatar src={image} size="medium" />}
        <span className="text-2xs text-foreground leading-normal">
          {formatLongString(name, maxLength)}
        </span>
      </div>
    )
  }
)

CuratorCell.displayName = 'CuratorCell'

export { CuratorCell }
